import { useState, useCallback, useEffect } from "react";
import { cartService } from "../services/cartService";
import { authService } from "../services/auth"; // Customer ID için

export const useCart = () => {
  const [cartItems, setCartItems] = useState([]);
  const [totalAmount, setTotalAmount] = useState("0.00");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  // Sepete ekleme işlemi için ayrı state'ler (opsiyonel)
  const [isAddingItem, setIsAddingItem] = useState(false);
  const [addItemError, setAddItemError] = useState(null);

  const fetchCart = useCallback(async () => {
    console.log("useCart - fetchCart çağrıldı.");
    const currentUser = authService.getCurrentUser();
    const customerId = currentUser?.id;
    console.log("useCart - customerId:", customerId);

    if (!customerId) {
      setError(
        "Sepeti getirmek için giriş yapılmalı veya kullanıcı ID bulunamadı."
      );
      setCartItems([]);
      setTotalAmount("0.00");
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const cartData = await cartService.getCustomerCart(customerId);
      console.log("useCart - Gelen Cart Data (Ham Yanıt):", cartData);

      let items = [];
      let calculatedTotal = 0;

      // Gelen yanıtın doğrudan dizi olup olmadığını kontrol et
      if (Array.isArray(cartData)) {
        items = cartData;
        // Toplam tutarı manuel hesapla
        calculatedTotal = items.reduce((sum, item) => {
          const price = parseFloat(item.price) || 0;
          const quantity = parseInt(item.quantity) || 0;
          // Eğer API'de item bazında total varsa onu kullanabiliriz (örn: item.total_item_price)
          return sum + price * quantity;
        }, 0);
      } else if (cartData && Array.isArray(cartData.items)) {
        // Eski { items: [], total_amount: ... } yapısı için fallback
        items = cartData.items;
        calculatedTotal = parseFloat(cartData.total_amount) || 0;
        console.warn(
          "useCart: Beklenen dizi yerine obje yanıtı alındı, işleniyor."
        );
      } else {
        console.warn("useCart: Beklenmeyen sepet yanıtı yapısı:", cartData);
      }

      setCartItems(items);
      // Hesaplanan veya alınan toplamı formatla
      setTotalAmount(calculatedTotal.toFixed(2));
    } catch (err) {
      setError(err.message || "Sepet getirilemedi");
      console.error("Sepet getirme hatası:", err);
      setCartItems([]);
      setTotalAmount("0.00");
    } finally {
      setLoading(false);
    }
  }, []);

  // Sepete ürün ekleme fonksiyonu (Optimistic Update)
  const addItemToCart = useCallback(
    async (dealerId, productId, productInfo = {}) => {
      const currentUser = authService.getCurrentUser();
      const customerId = currentUser?.id;

      if (!customerId || !dealerId || !productId) {
        console.error("addItemToCart: Eksik parametreler!", {
          customerId,
          dealerId,
          productId,
        });
        setAddItemError("Sepete eklemek için gerekli bilgiler eksik.");
        return false;
      }

      // Optimistic update: Önce state'i güncelle
      const existingItemIndex = cartItems.findIndex(
        item => item.dealer_id === dealerId && item.product_id === productId
      );

      let updatedItems;
      let previousItems = [...cartItems];
      let previousTotal = totalAmount;

      if (existingItemIndex > -1) {
        // Ürün zaten sepette, miktarı artır
        updatedItems = [...cartItems];
        const existingItem = updatedItems[existingItemIndex];
        const newQuantity = parseInt(existingItem.quantity) + 1;
        const itemPrice = parseFloat(existingItem.price) || 0;
        
        updatedItems[existingItemIndex] = {
          ...existingItem,
          quantity: newQuantity.toString(),
          total_item_price: (itemPrice * newQuantity).toFixed(2)
        };
      } else {
        // Yeni ürün ekle
        const newItem = {
          cart_item_id: `temp_${Date.now()}`, // Geçici ID
          customer_id: customerId,
          dealer_id: dealerId,
          product_id: productId,
          product_name: productInfo.product_name || "Ürün",
          dealer_name: productInfo.dealer_name || "",
          price: productInfo.price || "0",
          quantity: "1",
          total_item_price: productInfo.price || "0",
          product_image_url: productInfo.product_image_url || "",
          ...productInfo
        };
        updatedItems = [...cartItems, newItem];
      }

      // Total'ı güncelle
      const newTotal = updatedItems.reduce((sum, item) => {
        return sum + (parseFloat(item.total_item_price) || 0);
      }, 0);

      // State'i hemen güncelle (UI anında güncellenir)
      setCartItems(updatedItems);
      setTotalAmount(newTotal.toFixed(2));
      setIsAddingItem(false);
      setAddItemError(null);

      try {
        // API çağrısını yap
        await cartService.addToCart(customerId, dealerId, productId);
        console.log("useCart: Ürün sepete başarıyla eklendi.");
        
        // Başarılı - state zaten güncel
        return true;
      } catch (err) {
        console.error("Sepete ekleme hatası (useCart):", err);
        
        // Hata durumunda eski state'e geri dön
        setCartItems(previousItems);
        setTotalAmount(previousTotal);
        setAddItemError(err.message || "Ürün sepete eklenemedi.");
        
        // Opsiyonel: Tam sepeti yeniden yükle
        // await fetchCart();
        
        return false;
      }
    },
    [cartItems, totalAmount]
  );

  // Sepetten ürün kaldırma fonksiyonu (Optimistic Update)
  const removeItemFromCart = useCallback(
    async (dealerId, productId) => {
      const currentUser = authService.getCurrentUser();
      const customerId = currentUser?.id;

      if (!customerId || !dealerId || !productId) {
        console.error("removeItemFromCart: Eksik parametreler!", {
          customerId,
          dealerId,
          productId,
        });
        return false;
      }

      // Önceki state'i sakla
      const previousItems = [...cartItems];
      const previousTotal = totalAmount;

      // Optimistic update: Ürünü hemen kaldır
      const updatedItems = cartItems.filter(
        item => !(item.dealer_id === dealerId && item.product_id === productId)
      );

      // Total'ı güncelle
      const newTotal = updatedItems.reduce((sum, item) => {
        return sum + (parseFloat(item.total_item_price) || 0);
      }, 0);

      // State'i hemen güncelle
      setCartItems(updatedItems);
      setTotalAmount(newTotal.toFixed(2));

      try {
        await cartService.deleteFromCart(customerId, dealerId, productId);
        console.log("useCart: Ürün sepetten başarıyla kaldırıldı.");
        return true;
      } catch (err) {
        console.error("Sepetten kaldırma hatası (useCart):", err);
        
        // Hata durumunda eski state'e geri dön
        setCartItems(previousItems);
        setTotalAmount(previousTotal);
        
        return false;
      }
    },
    [cartItems, totalAmount]
  );

  // Miktar güncelleme fonksiyonu (Optimistic Update)
  const updateItemQuantity = useCallback(
    async (dealerId, productId, newQuantity) => {
      const currentUser = authService.getCurrentUser();
      const customerId = currentUser?.id;

      if (!customerId || !dealerId || !productId || newQuantity < 1) {
        console.error("updateItemQuantity: Geçersiz parametreler!");
        return false;
      }

      // Önceki state'i sakla
      const previousItems = [...cartItems];
      const previousTotal = totalAmount;

      // Optimistic update: Miktarı hemen güncelle
      const updatedItems = cartItems.map(item => {
        if (item.dealer_id === dealerId && item.product_id === productId) {
          const itemPrice = parseFloat(item.price) || 0;
          return {
            ...item,
            quantity: newQuantity.toString(),
            total_item_price: (itemPrice * newQuantity).toFixed(2)
          };
        }
        return item;
      });

      // Total'ı güncelle
      const newTotal = updatedItems.reduce((sum, item) => {
        return sum + (parseFloat(item.total_item_price) || 0);
      }, 0);

      // State'i hemen güncelle
      setCartItems(updatedItems);
      setTotalAmount(newTotal.toFixed(2));

      try {
        await cartService.updateCartItemQuantity(
          customerId,
          dealerId,
          productId,
          newQuantity
        );
        console.log("useCart: Ürün miktarı güncellendi.");
        return true;
      } catch (err) {
        console.error("Miktar güncelleme hatası (useCart):", err);
        
        // Hata durumunda eski state'e geri dön
        setCartItems(previousItems);
        setTotalAmount(previousTotal);
        
        return false;
      }
    },
    [cartItems, totalAmount]
  );

  // İlk yüklemede veya kullanıcı değiştiğinde sepeti getir (opsiyonel)
  useEffect(() => {
    const isAuthenticated = authService.isAuthenticated();
    if (isAuthenticated) {
      fetchCart();
    } else {
      // Giriş yapılmamışsa sepeti temizle
      setCartItems([]);
      setTotalAmount("0.00");
      setError(null);
    }
  }, [fetchCart]); // Belki auth durumuna da bağlanmalı?

  return {
    cartItems,
    totalAmount,
    loading,
    error,
    refetchCart: fetchCart,
    // Sepete ekleme ile ilgili state ve fonksiyonlar
    addItemToCart,
    isAddingItem,
    addItemError,
    // Sepetten kaldırma ve güncelleme fonksiyonları
    removeItemFromCart,
    updateItemQuantity,
  };
};
